import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

export type AppColorTheme = 'light' | 'dark';

type ThemeContextValue = {
  theme: AppColorTheme;
  resolvedTheme: AppColorTheme;
  setTheme: (theme: AppColorTheme) => void;
  themes: AppColorTheme[];
};

const THEMES: AppColorTheme[] = ['light', 'dark'];

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Theme is scoped to a single browser tab via sessionStorage. Unlike
// localStorage, sessionStorage is not shared across tabs, so there is no
// cross-tab `storage` event to fight over.
const STORAGE_KEY = 'accio-theme';

function readStoredTheme(): AppColorTheme | null {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function applyTheme(theme: AppColorTheme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
}: PropsWithChildren<{ defaultTheme?: AppColorTheme }>) {
  const [theme, setThemeState] = useState<AppColorTheme>(() => readStoredTheme() ?? defaultTheme);

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: AppColorTheme) => {
    setThemeState(next);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme: theme, setTheme, themes: THEMES }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used within a ThemeProvider');
  return value;
}
